import styles from './SyntaxHighlightedCode.module.css';

type TokenType =
  | 'comment'
  | 'string'
  | 'number'
  | 'keyword'
  | 'builtin'
  | 'function'
  | 'identifier'
  | 'operator';

interface SyntaxHighlightedCodeProps {
  code: string;
  ariaHidden?: boolean;
  className?: string;
}

interface Token {
  type: TokenType;
  value: string;
}

const KEYWORDS = new Set([
  'async',
  'await',
  'break',
  'case',
  'catch',
  'class',
  'const',
  'continue',
  'default',
  'do',
  'else',
  'export',
  'extends',
  'finally',
  'for',
  'from',
  'function',
  'if',
  'import',
  'in',
  'instanceof',
  'let',
  'new',
  'of',
  'return',
  'switch',
  'throw',
  'try',
  'typeof',
  'var',
  'while',
]);

const BUILT_INS = new Set([
  'Array',
  'Boolean',
  'Date',
  'Error',
  'JSON',
  'Map',
  'Math',
  'Number',
  'Object',
  'Promise',
  'RegExp',
  'Set',
  'String',
  'URL',
  'URLSearchParams',
  'console',
  'document',
  'fetch',
  'localStorage',
  'window',
]);

const TOKEN_PATTERN =
  /\/\/[^\n]*|\/\*[\s\S]*?\*\/|`(?:\\[\s\S]|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b\d+(?:\.\d+)?\b|\b[A-Za-z_$][\w$]*\b|[{}()[\].,;:+\-*/%=<>!&|?]+/g;

export default function SyntaxHighlightedCode({
  code,
  ariaHidden = false,
  className = '',
}: SyntaxHighlightedCodeProps) {
  const classNames = [styles.code, className].filter(Boolean).join(' ');

  return (
    <pre className={classNames} aria-hidden={ariaHidden || undefined}>
      <code>
        {tokenizeJavaScript(code).map((token, index) => (
          <span key={`${token.value}-${index}`} className={styles[token.type]}>
            {token.value}
          </span>
        ))}
      </code>
    </pre>
  );
}

function tokenizeJavaScript(source: string): Token[] {
  const tokens: Token[] = [];
  let cursor = 0;
  TOKEN_PATTERN.lastIndex = 0;

  let match = TOKEN_PATTERN.exec(source);
  while (match) {
    const value = match[0];
    const index = match.index;

    if (index > cursor) {
      tokens.push({ type: 'identifier', value: source.slice(cursor, index) });
    }

    tokens.push({ type: classifyToken(value, source, index), value });
    cursor = index + value.length;
    match = TOKEN_PATTERN.exec(source);
  }

  if (cursor < source.length) {
    tokens.push({ type: 'identifier', value: source.slice(cursor) });
  }

  return tokens;
}

function classifyToken(value: string, source: string, index: number): TokenType {
  if (value.startsWith('//') || value.startsWith('/*')) return 'comment';
  if (value.startsWith('"') || value.startsWith("'") || value.startsWith('`')) return 'string';
  if (/^\d/.test(value)) return 'number';
  if (KEYWORDS.has(value)) return 'keyword';
  if (BUILT_INS.has(value)) return 'builtin';
  if (/^[{}()[\].,;:+\-*/%=<>!&|?]+$/.test(value)) return 'operator';

  const afterToken = source.slice(index + value.length);
  if (/^\s*\(/.test(afterToken)) return 'function';

  return 'identifier';
}
